import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from "@azure/msal-react";
import { EventType, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";

// AuthContext.js
export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);
  const configRef = useRef(null);

  const readScope = useCallback(async () => {
    if (!configRef.current) {
      const res = await fetch('/api/config');
      configRef.current = await res.json();
    }
    return configRef.current.SP_API_ENDPOINT; // e.g. api://.../access
  }, []);

  const setActiveFromCache = useCallback(() => {
    const accts = (accounts && accounts.length > 0) ? accounts : instance.getAllAccounts();
    if (accts.length > 0) {
      const current = instance.getActiveAccount();
      if (!current) instance.setActiveAccount(accts[0]);
      return instance.getActiveAccount() || accts[0];
    }
    return null;
  }, [accounts, instance]);

  const needsInteractiveLogin = useCallback((error) => {
    if (!error) {
      return false;
    }
    const code = error.errorCode || error.subError || '';
    if (
      error instanceof InteractionRequiredAuthError ||
      (typeof InteractionRequiredAuthError.isInteractionRequiredError === 'function' &&
        InteractionRequiredAuthError.isInteractionRequiredError(code))
    ) {
      return true;
    }

    return ['monitor_window_timeout', 'token_renewal_error', 'no_tokens_found'].includes(code);
  }, []);

  const acquireTokenResilient = useCallback(async () => {
    const account = setActiveFromCache();
    const scope = await readScope();

    if (!account) {
      if (inProgress !== InteractionStatus.None) {
        return null;
      }
      // No cached session -> interactive login
      try {
        const res = await instance.loginPopup({ scopes: [scope] });
        if (res.account) instance.setActiveAccount(res.account);
      } catch (e) {
        if (inProgress !== InteractionStatus.None) {
          return null;
        }
        // Popup blocked? Fall back to redirect
        await instance.loginRedirect({ scopes: [scope] });
        return null; // execution will continue after redirect
      }
    }

    try {
      const res = await instance.acquireTokenSilent({ scopes: [scope], account: instance.getActiveAccount() });
      return res;
    } catch (e) {
      if (!needsInteractiveLogin(e)) {
        throw e;
      }
      if (inProgress !== InteractionStatus.None) {
        return null;
      }
      try {
        const res = await instance.acquireTokenPopup({ scopes: [scope], account: instance.getActiveAccount() || undefined });
        return res;
      } catch (popupErr) {
        if (inProgress !== InteractionStatus.None) {
          return null;
        }
        await instance.acquireTokenRedirect({ scopes: [scope], account: instance.getActiveAccount() || undefined });
        return null; // will resume after redirect
      }
    }
  }, [instance, setActiveFromCache, readScope, inProgress, needsInteractiveLogin]);

  const init = useCallback(async () => {
    try {
      const tokenResp = await acquireTokenResilient();
      if (!tokenResp) return; // redirect path

      tokenRef.current = {
        token: tokenResp.accessToken,
        // subtract 5 minutes to proactively refresh
        expiresAt: tokenResp.expiresOn ? tokenResp.expiresOn.getTime() - 5 * 60 * 1000 : Date.now() + 55 * 60 * 1000,
      };
      setLoading(false);
    } catch (err) {
      console.error('Erreur AuthContext init:', err);
      setLoading(false);
    }
  }, [acquireTokenResilient]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      init();
    }
  }, [init, inProgress]);

  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
      }
      if (event.eventType === EventType.HANDLE_REDIRECT_END) {
        // After redirect completes, try to init again
        init();
      }
    });

    return () => {
      if (cbId) instance.removeEventCallback(cbId);
    };
  }, [instance, init]);

  const getToken = async () => {
    const now = Date.now();
    const scope = await readScope();

    // serve from cache if still valid
    if (tokenRef.current && tokenRef.current.expiresAt > now) {
      return tokenRef.current.token;
    }

    // try silent, then popup, then redirect
    const account = setActiveFromCache();
    try {
      const res = await instance.acquireTokenSilent({ scopes: [scope], account: account || undefined });
      tokenRef.current = {
        token: res.accessToken,
        expiresAt: res.expiresOn ? res.expiresOn.getTime() - 5 * 60 * 1000 : now + 55 * 60 * 1000,
      };
      return res.accessToken;
    } catch (e) {
      if (!needsInteractiveLogin(e)) {
        throw e;
      }
      if (inProgress !== InteractionStatus.None) {
        return '';
      }
      try {
        const res = await instance.acquireTokenPopup({ scopes: [scope], account: account || undefined });
        tokenRef.current = {
          token: res.accessToken,
          expiresAt: res.expiresOn ? res.expiresOn.getTime() - 5 * 60 * 1000 : now + 55 * 60 * 1000,
        };
        return res.accessToken;
      } catch (popupErr) {
        if (inProgress !== InteractionStatus.None) {
          return '';
        }
        await instance.acquireTokenRedirect({ scopes: [scope], account: account || undefined });
        return '';
      }
    }
  };

  return (
    <AuthContext.Provider value={{ getToken, loading, account: instance.getActiveAccount() || instance.getAllAccounts()[0] || null }}>
      {children}
    </AuthContext.Provider>
  );
}
